import ServiceFeeNote from "@/components/ServiceFeeNote";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { startCoinPayment } from "@/lib/coinpay";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Pill } from "@/components/perf";
import { TierIcon } from "@/components/tierIcons";
import { toast } from "sonner";
import { Check, Crown, Lock, ArrowLeft, Sparkles } from "lucide-react";

export default function Upgrade() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const reason = params.get("reason");
  const [tiers, setTiers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/membership/tiers").then(({ data }) => setTiers(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  const current = user?.membership || "free";
  const curIdx = tiers.findIndex((t) => t.id === current);
  const upgrades = curIdx === -1 ? tiers.filter((t) => t.price > 0) : tiers.slice(curIdx + 1);
  const currentTier = tiers[curIdx];

  const subscribe = async (t) => {
    setBusy(true);
    try {
      if (!t.price) {
        await api.post("/membership/subscribe", { tier: t.id });
        toast.success(`Switched to ${t.name} membership`);
        navigate("/profile");
      } else {
        const { data } = await api.post("/payments/checkout", { package_id: t.id, origin_url: window.location.origin });
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="upgrade-page">
      <button data-testid="upgrade-back-btn" onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-pb-red transition-colors mb-6">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <PageHeader ck="page.upgrade" kicker="Membership · Level Up"
        title="Upgrade Your Membership"
        subtitle="Unlock more of the Bullpen — league play, unlimited AI film analysis, live recording, and everything your next tier includes." />

      {reason && (
        <div data-testid="upgrade-reason" className="border border-pb-red/50 bg-pb-red/5 p-4 mb-8 flex items-center gap-3 animate-fade-up max-w-3xl">
          <Lock className="w-5 h-5 text-pb-red shrink-0" />
          <div>
            <div className="font-display font-bold uppercase text-sm">Locked Feature</div>
            <div className="text-sm text-zinc-400">{reason}</div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 mb-8" data-testid="upgrade-current-tier">
        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Your current tier</span>
        <span className="flex items-center gap-2 border border-pb-border bg-pb-surface px-3 py-1.5 font-display font-black uppercase">
          {currentTier && <TierIcon icon={currentTier.icon} className="w-4 h-4 text-pb-red" />}
          {currentTier?.name || current}
        </span>
      </div>

      {upgrades.length === 0 ? (
        <div data-testid="upgrade-top-tier" className="bg-pb-surface border border-pb-red/40 p-10 text-center max-w-2xl">
          <Crown className="w-10 h-10 text-pb-red mx-auto mb-3" />
          <div className="font-display font-black text-2xl uppercase mb-1">You're At The Top</div>
          <p className="text-sm text-zinc-400">You already hold the highest membership tier — everything in the Bullpen is unlocked.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl">
          {upgrades.map((t, i) => (
            <div key={t.id} data-testid={`upgrade-tier-${t.id}`}
              className={`bg-pb-surface border p-6 flex flex-col ${i === 0 ? "border-pb-red shadow-[0_0_24px_rgba(239,68,68,0.12)]" : "border-pb-cyan/40"}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-display font-black text-2xl uppercase flex items-center gap-2">
                  <TierIcon icon={t.icon} className="w-5 h-5 text-pb-red" />
                  {t.name}
                </span>
                {i === 0 && <Pill color="#A3E635"><span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> Next Tier</span></Pill>}
              </div>
              <div className="font-display font-black text-3xl text-pb-cyan mb-4">${t.price}<span className="text-sm text-zinc-500">{t.recurring ? "/mo" : ""}</span></div>
              <ul className="space-y-2 mb-6 flex-1">
                {t.perks.map((p, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-zinc-300">
                    <Check className="w-4 h-4 text-pb-cyan mt-0.5 shrink-0" /> {p}
                  </li>
                ))}
              </ul>
              <button data-testid={`upgrade-select-${t.id}`} onClick={() => subscribe(t)} disabled={busy}
                className="w-full bg-pb-red text-black font-bold uppercase tracking-widest py-3 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
                {t.price > 0 ? `Upgrade · $${t.price}${t.recurring ? "/mo" : ""}` : `Switch to ${t.name}`}
              </button>
              {t.price > 0 && <ServiceFeeNote className="text-center mt-1.5" />}
              {t.price > 0 && t.price_coins && (
                <button data-testid={`upgrade-bullion-${t.id}`} onClick={() => startCoinPayment(navigate, { kind: "membership", item_id: t.id })} disabled={busy}
                  className="w-full mt-2 border border-purple-400/60 text-purple-300 hover:bg-purple-400/10 font-bold uppercase tracking-widest py-2.5 text-xs active:scale-95 transition-all disabled:opacity-50">
                  🪙 Pay with Coin · {t.price_coins} BLC
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] text-zinc-600 mt-8 max-w-3xl">
        Memberships are account access tiers and are separate from drill session passes (see the Book tab). You can manage or change your tier anytime from your Player Profile.
      </p>
    </div>
  );
}
